import React, { useState } from 'react'

import Modal, { ActionPanel } from '~/components/modal'
import { i18n } from '~/i18n'

import {
  StyledButton,
  BoldOriginalAddress,
  NotValidatedPill,
  StyledContentBlock,
  SelectableOption,
  TickIcon,
  SelectableOptionTitle,
  SmallAddress,
  Content,
} from './address-verification-modal.styles'

import type { Address } from '~/types'

import './address-verification-modal.scss'

type Props = {
  verificationStatus: string
  changedFields: Array<string>
  suggestedAddress: Address
  originalAddress: Address
  acceptOriginalAddress: () => any
  acceptSuggestedAddress: () => any
  reenterAddress: () => any
}

export default function AddressVerificationModal({
  verificationStatus,
  changedFields,
  suggestedAddress,
  originalAddress,
  acceptOriginalAddress,
  acceptSuggestedAddress,
  reenterAddress,
}: Props) {
  const [selectedOption, setSelectedOption] = useState(null)

  const onContinue = () => {
    if (selectedOption === 'original_address') {
      acceptOriginalAddress()
    } else if (selectedOption === 'reenter_address') {
      reenterAddress()
    } else if (selectedOption === 'suggested_address') {
      acceptSuggestedAddress()
    }
  }

  return (
    <Modal
      className="address-verification-modal"
      title={i18n.t('components.address_verification_modal.title')}
      actionPanel={
        <ActionPanel>
          <StyledButton
            disabled={!selectedOption}
            onClick={onContinue}
            data-testid="continue"
          >
            {i18n.t('components.address_verification_modal.continue')}
          </StyledButton>
        </ActionPanel>
      }
      onClose={reenterAddress}
    >
      <Content>
        <BoldOriginalAddress address={originalAddress} />
        <NotValidatedPill>
          {i18n.t(
            'components.address_verification_modal.address_not_validated',
          )}
        </NotValidatedPill>
        <StyledContentBlock>
          {verificationStatus == 'suggest_change'
            ? i18n.t('components.address_verification_modal.suggest_address_md')
            : i18n.t('components.address_verification_modal.not_verified_md')}
        </StyledContentBlock>
        {verificationStatus == 'suggest_change' && (
          <SelectableOption
            data-testid="suggested_address"
            selected={selectedOption === 'suggested_address'}
            onClick={() => setSelectedOption('suggested_address')}
          >
            <TickIcon $dark={selectedOption === 'suggested_address'} />
            <div>
              <SelectableOptionTitle>
                {i18n.t(
                  'components.address_verification_modal.use_recommended_address',
                )}
              </SelectableOptionTitle>
              <SmallAddress address={suggestedAddress} />
            </div>
          </SelectableOption>
        )}
        <SelectableOption
          data-testid="original_address"
          selected={selectedOption === 'original_address'}
          onClick={() => setSelectedOption('original_address')}
        >
          <TickIcon $dark={selectedOption === 'original_address'} />
          <div>
            <SelectableOptionTitle>
              {i18n.t(
                'components.address_verification_modal.use_original_address',
              )}
            </SelectableOptionTitle>
            <SmallAddress
              address={originalAddress}
              highlightedFields={changedFields}
            />
          </div>
        </SelectableOption>
        <SelectableOption
          data-testid="reenter_address"
          selected={selectedOption === 'reenter_address'}
          onClick={() => setSelectedOption('reenter_address')}
        >
          <TickIcon $dark={selectedOption === 'reenter_address'} />
          <div>
            <SelectableOptionTitle>
              {i18n.t('components.address_verification_modal.reenter_address')}
            </SelectableOptionTitle>
          </div>
        </SelectableOption>
      </Content>
    </Modal>
  )
}
